import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import API from '../../../services/API'
import PermissionCheck from '../../../helpers/PermissionCheck'
import { ANNOUNCEMENT_TYPES, typeColor } from './announcementConstants'

// @MUI
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Chip from '@mui/material/Chip'
import InputAdornment from '@mui/material/InputAdornment'
import LinearProgress from '@mui/material/LinearProgress'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined'
import SearchIcon from '@mui/icons-material/Search'
import TaskAltIcon from '@mui/icons-material/TaskAlt'

const PRESETS = [
  {
    key: 'unread',
    label: 'Unread',
    caption: 'New to you',
    tone: 'info',
  },
  {
    key: 'action',
    label: 'Needs Acknowledgement',
    caption: 'Awaiting your sign-off',
    tone: 'warning',
  },
  {
    key: 'all',
    label: 'All Announcements',
    caption: 'Everything on record',
    tone: 'primary',
  },
]

const PresetTile = ({ preset, count, active, onClick }) => (
  <Card
    variant="outlined"
    onClick={onClick}
    sx={{
      p: 1.5,
      borderRadius: 2.5,
      cursor: 'pointer',
      minWidth: 0,
      borderColor: active ? 'primary.main' : 'divider',
      borderWidth: active ? 2 : 1,
      bgcolor: active ? 'action.hover' : 'background.paper',
      transition: 'all 0.15s ease',
      '&:hover': { borderColor: 'primary.light' },
    }}
  >
    <Typography
      component="div"
      variant="caption"
      noWrap
      sx={{
        color: 'text.secondary',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        fontSize: '0.66rem',
      }}
    >
      {preset.label}
    </Typography>
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: `${preset.tone}.main`,
          flexShrink: 0,
        }}
      />
      <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }} noWrap>
        {count ?? '—'}
      </Typography>
    </Stack>
    <Typography component="div" variant="caption" color="text.secondary" noWrap>
      {preset.caption}
    </Typography>
  </Card>
)

const AnnouncementCard = ({ announcement, onOpen }) => {
  const needsAck =
    announcement.requiresAcknowledgement &&
    !announcement.isAcknowledgedByCurrentUser

  const isUnread =
    !announcement.requiresAcknowledgement &&
    !announcement.isAcknowledgedByCurrentUser

  const accent = typeColor(announcement.type)

  return (
    <Paper
      elevation={0}
      onClick={onOpen}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        p: { xs: 1.5, md: 2 },
        pl: { xs: 2.25, md: 2.75 },
        borderRadius: 2.5,
        border: (t) => `1px solid ${t.palette.divider}`,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        '&:hover': {
          borderColor: 'primary.light',
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
          transform: 'translateY(-1px)',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          bgcolor: `${accent}.main`,
        },
      }}
    >
      <Stack gap={0.75}>
        <Stack
          direction="row"
          gap={1}
          alignItems="flex-start"
          justifyContent="space-between"
          flexWrap="wrap"
          useFlexGap
        >
          <Stack
            direction="row"
            gap={1}
            alignItems="center"
            minWidth={0}
            flexWrap="wrap"
            useFlexGap
          >
            {(isUnread || needsAck) && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: needsAck ? 'error.main' : 'info.main',
                  flexShrink: 0,
                }}
              />
            )}
            <Typography
              sx={{
                fontWeight: isUnread || needsAck ? 800 : 600,
                fontSize: '1rem',
              }}
              noWrap
            >
              {announcement.title}
            </Typography>
            {announcement.type && (
              <Chip
                size="small"
                color={accent}
                label={announcement.type}
                sx={{ borderRadius: 999, fontWeight: 600, height: 20 }}
              />
            )}
            {needsAck && (
              <Chip
                size="small"
                color="error"
                variant="outlined"
                label="Needs Acknowledgement"
                sx={{ borderRadius: 999, fontWeight: 600, height: 20 }}
              />
            )}
            {isUnread && (
              <Chip
                size="small"
                color="info"
                variant="outlined"
                label="Unread"
                sx={{ borderRadius: 999, fontWeight: 600, height: 20 }}
              />
            )}
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ flexShrink: 0 }}
            title={moment(announcement.createdAt).format('Do MMMM YYYY HH:mm')}
          >
            {moment(announcement.createdAt).fromNow()}
          </Typography>
        </Stack>

        {announcement.detail && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {announcement.detail}
          </Typography>
        )}

        <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
          <Stack direction="row" gap={0.75} alignItems="center" minWidth={0}>
            <Avatar
              src={announcement?.author?.photograph}
              sx={{ width: 20, height: 20, fontSize: '0.6rem' }}
            >
              {announcement?.author?.firstName?.[0]}
              {announcement?.author?.lastName?.[0]}
            </Avatar>
            <Typography variant="caption" color="text.secondary" noWrap>
              {announcement?.author?.firstName} {announcement?.author?.lastName}
            </Typography>
          </Stack>
          {(announcement.attachments?.length ?? 0) > 0 && (
            <Stack direction="row" gap={0.25} alignItems="center">
              <AttachFileIcon
                sx={{ fontSize: '0.9rem', color: 'text.secondary' }}
              />
              <Typography variant="caption" color="text.secondary">
                {announcement.attachments.length} attachment
                {announcement.attachments.length === 1 ? '' : 's'}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Paper>
  )
}

const Announcements = () => {
  const [announcements, setAnnouncements] = useState([])
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [activePreset, setActivePreset] = useState('all')
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState(null)
  const navigate = useNavigate()

  useEffect(() => {
    API.post('/governance/get-announcements', {})
      .then((res) => {
        if (res.status === 200) {
          setAnnouncements(res.data)
        } else {
          setFetchError(true)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [])

  const counts = useMemo(
    () => ({
      all: announcements.length,
      unread: announcements.filter((a) => !a.isAcknowledgedByCurrentUser)
        .length,
      action: announcements.filter(
        (a) => a.requiresAcknowledgement && !a.isAcknowledgedByCurrentUser,
      ).length,
    }),
    [announcements],
  )

  const visibleAnnouncements = useMemo(() => {
    const term = search.trim().toLowerCase()
    return announcements.filter((a) => {
      if (activePreset === 'unread' && a.isAcknowledgedByCurrentUser)
        return false
      if (
        activePreset === 'action' &&
        (!a.requiresAcknowledgement || a.isAcknowledgedByCurrentUser)
      )
        return false
      if (typeFilter && a.type !== typeFilter) return false
      if (term) {
        const haystack = [
          a.title,
          a.detail,
          a.type,
          a.author?.firstName,
          a.author?.lastName,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        if (!haystack.includes(term)) return false
      }
      return true
    })
  }, [announcements, activePreset, typeFilter, search])

  return (
    <Stack gap={1.5}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        gap={1}
      >
        <Box>
          <Typography variant="title2">Announcements</Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            {loading
              ? ''
              : counts.action > 0
                ? `${counts.action} announcement${
                    counts.action === 1 ? '' : 's'
                  } awaiting your acknowledgement`
                : counts.unread > 0
                  ? `${counts.unread} unread announcement${
                      counts.unread === 1 ? '' : 's'
                    }`
                  : 'You are all caught up'}
          </Typography>
        </Box>
        <PermissionCheck roles={['Manager', 'Supervisor', 'Governance']}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/announcements/add-new')}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            New Announcement
          </Button>
        </PermissionCheck>
      </Stack>

      <Box
        display="grid"
        gap={1.5}
        gridTemplateColumns={{ xs: '1fr', sm: 'repeat(3, 1fr)' }}
      >
        {PRESETS.map((preset) => (
          <PresetTile
            key={preset.key}
            preset={preset}
            count={loading ? undefined : counts[preset.key]}
            active={activePreset === preset.key}
            onClick={() => setActivePreset(preset.key)}
          />
        ))}
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 1.25,
          borderRadius: 2.5,
          border: (t) => `1px solid ${t.palette.divider}`,
        }}
      >
        <Stack
          direction="row"
          gap={1}
          flexWrap="wrap"
          alignItems="center"
          useFlexGap
        >
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            placeholder="Search title, content, author…"
            sx={{ flex: 1, minWidth: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction="row" gap={0.75} flexWrap="wrap" useFlexGap>
            {ANNOUNCEMENT_TYPES.map((type) => (
              <Chip
                key={type}
                label={type}
                size="small"
                clickable
                color={typeFilter === type ? typeColor(type) : 'default'}
                variant={typeFilter === type ? 'filled' : 'outlined'}
                onClick={() =>
                  setTypeFilter((prev) => (prev === type ? null : type))
                }
                sx={{ borderRadius: 999, fontWeight: 600 }}
              />
            ))}
          </Stack>
        </Stack>
      </Paper>

      {loading && <LinearProgress />}
      {fetchError && (
        <Alert severity="error">
          Unable to load announcements. Please try again later.
        </Alert>
      )}

      {!loading && !fetchError && visibleAnnouncements.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 2.5,
            border: (t) => `1px dashed ${t.palette.divider}`,
            textAlign: 'center',
          }}
        >
          {activePreset !== 'all' && !search && !typeFilter ? (
            <Stack alignItems="center" gap={1}>
              <TaskAltIcon sx={{ fontSize: 40, color: 'success.main' }} />
              <Typography fontWeight={700}>All caught up!</Typography>
              <Typography variant="body2" color="text.secondary">
                Nothing here needs your attention right now.
              </Typography>
            </Stack>
          ) : announcements.length === 0 ? (
            <Stack alignItems="center" gap={1}>
              <CampaignOutlinedIcon
                sx={{ fontSize: 40, color: 'text.secondary' }}
              />
              <Typography fontWeight={700}>No announcements yet</Typography>
              <Typography variant="body2" color="text.secondary">
                Company-wide notices published by managers appear here for all
                staff.
              </Typography>
            </Stack>
          ) : (
            <Alert severity="warning" sx={{ justifyContent: 'center' }}>
              <AlertTitle>No Matching Results</AlertTitle>
              Please adjust your search or filters and try again.
            </Alert>
          )}
        </Paper>
      )}

      <Stack gap={1}>
        {visibleAnnouncements.map((announcement) => (
          <AnnouncementCard
            key={announcement._id}
            announcement={announcement}
            onOpen={() => navigate('/announcements/' + announcement._id)}
          />
        ))}
      </Stack>
    </Stack>
  )
}

export default Announcements
